import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadProductByBarcodeUrl,
  zadProductByIdUrl,
  zadProductGenerateBarcodeUrl,
  zadProductRestoreUrl,
  zadProductsImportUrl,
  zadProductsUrl,
  zadProductStatusUrl,
  zadProductUpdateUrl,
} from '../../api'
import {buildZadListQuery} from '../zad/query'
import {parseZadApiError} from '../zad/errors'
import {sanitizeZadProductImportPayload} from '../zad/productImportParse'
import {preserveZadBarcode, toZadProductPayload} from '../zad/productHelpers'
import {
  CreateZadProductRequest,
  FetchZadProductByBarcodeRequest,
  FetchZadProductByIdRequest,
  FetchZadProductsRequest,
  GenerateZadProductBarcodeRequest,
  ImportZadProductsRequest,
  RestoreZadProductRequest,
  UpdateZadProductRequest,
  UpdateZadProductStatusRequest,
} from './types'
import {attachZadProductImportOrigin} from '../zad/purchaseCancellationHelpers'
import {fetchZadStock} from '../zadStock/service'

export const fetchZadProducts = createAsyncThunk(
  'zadProducts/fetch',
  async (payload: FetchZadProductsRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadProductsUrl}?${buildZadListQuery(payload)}`)
      const {products, total, totalPages} = res.data || {}
      return {
        success: true as const,
        products: Array.isArray(products) ? products : [],
        total: typeof total === 'number' ? total : 0,
        totalPages: typeof totalPages === 'number' ? totalPages : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadProductById = createAsyncThunk(
  'zadProducts/fetchById',
  async (payload: FetchZadProductByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadProductByIdUrl(payload.id))
      return {
        success: true as const,
        product: res.data.product,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadProduct = createAsyncThunk(
  'zadProducts/create',
  async (payload: CreateZadProductRequest, {rejectWithValue}) => {
    try {
      const body = toZadProductPayload(payload)
      const res = await axios.post(zadProductsUrl, body)
      return {
        success: true as const,
        product: res.data.product,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadProduct = createAsyncThunk(
  'zadProducts/update',
  async (payload: UpdateZadProductRequest, {rejectWithValue}) => {
    try {
      const {id, ...values} = payload
      const body = toZadProductPayload(values)
      const res = await axios.post(zadProductUpdateUrl(id), body)
      return {
        success: true as const,
        product: res.data.product,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const importZadProducts = createAsyncThunk(
  'zadProducts/import',
  async (payload: ImportZadProductsRequest, {rejectWithValue}) => {
    try {
      const sanitized = sanitizeZadProductImportPayload(payload)
      const body = attachZadProductImportOrigin(sanitized, payload.originPurchase)
      const res = await axios.post(zadProductsImportUrl, body)
      return {
        success: true as const,
        imported: typeof res.data.imported === 'number' ? res.data.imported : body.products.length,
        products: Array.isArray(res.data.products) ? res.data.products : [],
        usedTransaction: res.data.usedTransaction,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadProductByBarcode = createAsyncThunk(
  'zadProducts/fetchByBarcode',
  async (payload: FetchZadProductByBarcodeRequest, {rejectWithValue}) => {
    try {
      const barcode = preserveZadBarcode(payload.barcode).trim()
      const res = await axios.get(zadProductByBarcodeUrl(barcode))
      return {
        success: true as const,
        product: res.data.product,
        selectedSaleUnit: res.data.selectedSaleUnit,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const generateZadProductBarcode = createAsyncThunk(
  'zadProducts/generateBarcode',
  async (payload: GenerateZadProductBarcodeRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadProductGenerateBarcodeUrl(payload.id), {})
      return {
        success: true as const,
        product: res.data.product,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const updateZadProductStatus = createAsyncThunk(
  'zadProducts/updateStatus',
  async (payload: UpdateZadProductStatusRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadProductStatusUrl(payload.id), {
        isActive: payload.isActive,
      })
      return {
        success: true as const,
        product: res.data.product,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const restoreZadProduct = createAsyncThunk(
  'zadProducts/restore',
  async (payload: RestoreZadProductRequest, {rejectWithValue, dispatch}) => {
    try {
      const res = await axios.post(zadProductRestoreUrl(payload.id), {})
      dispatch(
        fetchZadStock({
          pageNumber: 1,
          pageCount: 10,
          sortField: 'createdAt',
          sortOrder: 'desc',
        })
      )
      return {
        success: true as const,
        product: res.data.product,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
